import axios from 'axios';
import { AuthRequest, LoginRequest, AuthResponse, User, PasswordlessAuthRequest, PasswordlessLoginRequest, OTPVerificationRequest } from '../types/auth';

const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:3000';
const USER_POOL_ID = import.meta.env.VITE_USER_POOL_ID;
const USER_POOL_CLIENT_ID = import.meta.env.VITE_USER_POOL_CLIENT_ID;
const IDENTITY_POOL_ID = import.meta.env.VITE_IDENTITY_POOL_ID;
const REGION = import.meta.env.VITE_REGION || 'us-east-1';

console.log('API Base URL:', API_BASE_URL);
console.log('Environment variables:', {
  VITE_API_URL: import.meta.env.VITE_API_URL,
  VITE_ENVIRONMENT: import.meta.env.VITE_ENVIRONMENT,
  VITE_GIT_HASH: import.meta.env.VITE_GIT_HASH,
  VITE_GIT_BRANCH: import.meta.env.VITE_GIT_BRANCH,
  VITE_BUILD_DATE: import.meta.env.VITE_BUILD_DATE
});

const authApi = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add request interceptor for debugging
authApi.interceptors.request.use((config) => {
  console.log('Making API request:', {
    method: config.method,
    url: config.url,
    baseURL: config.baseURL,
    fullURL: `${config.baseURL}${config.url}`,
    data: config.data
  });
  return config;
});

// Add response interceptor for debugging
authApi.interceptors.response.use(
  (response) => {
    console.log('API response received:', {
      status: response.status,
      url: response.config.url,
      data: response.data
    });
    return response;
  },
  (error) => {
    console.error('API request failed:', {
      status: error.response?.status,
      url: error.config?.url,
      data: error.response?.data,
      message: error.message
    });
    return Promise.reject(error);
  }
);

// Add token to requests if available
authApi.interceptors.request.use((config) => {
  const token = localStorage.getItem('auth_token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

export class AuthService {
  static async register(data: AuthRequest): Promise<AuthResponse> {
    try {
      const response = await authApi.post('/auth', data);
      return response.data;
    } catch (error: any) {
      return {
        success: false,
        message: 'Registration failed',
        error: error.response?.data?.error || error.message,
      };
    }
  }

  static async login(data: LoginRequest): Promise<AuthResponse> {
    try {
      const response = await authApi.post('/auth/login', data);
      
      if (response.data.success && response.data.data?.token) {
        localStorage.setItem('auth_token', response.data.data.token);
        if (response.data.data.refreshToken) {
          localStorage.setItem('refresh_token', response.data.data.refreshToken);
        }
      }
      
      return response.data;
    } catch (error: any) {
      return {
        success: false,
        message: 'Login failed',
        error: error.response?.data?.error || error.message,
      };
    }
  }

  static async getUserProfile(): Promise<AuthResponse> {
    try {
      const token = localStorage.getItem('auth_token');
      if (!token) {
        return {
          success: false,
          message: 'No authentication token',
          error: 'User not authenticated',
        };
      }

      const response = await authApi.get('/auth/profile');
      return response.data;
    } catch (error: any) {
      return {
        success: false,
        message: 'Failed to get user profile',
        error: error.response?.data?.error || error.message,
      };
    }
  }

  static async confirmEmail(data: { email: string; code: string }): Promise<AuthResponse> {
    try {
      const response = await authApi.post('/auth/confirm-email', data);
      return response.data;
    } catch (error: any) {
      return {
        success: false,
        message: 'Email confirmation failed',
        error: error.response?.data?.error || error.message,
      };
    }
  }

  static async resendConfirmationCode(email: string): Promise<AuthResponse> {
    try {
      const response = await authApi.post('/auth/resend-confirmation', { email });
      return response.data;
    } catch (error: any) {
      return {
        success: false,
        message: 'Failed to resend confirmation code',
        error: error.response?.data?.error || error.message,
      };
    }
  }

  static async logout(): Promise<void> {
    try {
      await authApi.post('/auth/logout');
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      localStorage.removeItem('auth_token');
      localStorage.removeItem('refresh_token');
    }
  }

  static getStoredToken(): string | null {
    return localStorage.getItem('auth_token');
  }

  static isTokenValid(token: string): boolean {
    try {
      const payload = JSON.parse(atob(token.split('.')[1]));
      return payload.exp * 1000 > Date.now();
    } catch {
      return false;
    }
  }

  static getUserTypeFromToken(token: string): 'client' | 'provider' | 'admin' | null {
    try {
      const payload = JSON.parse(atob(token.split('.')[1]));
      
      // Check Cognito groups first (preferred method)
      if (payload['cognito:groups'] && payload['cognito:groups'].length > 0) {
        const groups = payload['cognito:groups'];
        if (groups.includes('admin')) return 'admin';
        if (groups.includes('provider')) return 'provider';
        if (groups.includes('client')) return 'client';
      }
      
      // Fallback to custom attribute
      if (payload['custom:userType']) {
        return payload['custom:userType'] as 'client' | 'provider' | 'admin';
      }
      
      return null;
    } catch {
      return null;
    }
  }

  // Get Cognito configuration for debugging
  static getCognitoConfig() {
    return {
      userPoolId: USER_POOL_ID,
      userPoolClientId: USER_POOL_CLIENT_ID,
      identityPoolId: IDENTITY_POOL_ID,
      region: REGION,
    };
  }

  static async getClientInfo(clientUuid: string): Promise<AuthResponse> {
    try {
      const response = await authApi.get(`/auth/client/${clientUuid}`);
      return response.data;
    } catch (error: any) {
      return {
        success: false,
        message: 'Failed to get client info',
        error: error.response?.data?.error || error.message,
      };
    }
  }

  static async startSession(sessionData: { clientId: string; timestamp: string }): Promise<AuthResponse> {
    try {
      console.log('Starting session with data:', sessionData);
      const requestData = {
        ...sessionData,
        action: 'start'
      };
      const response = await authApi.post('/checkin', requestData);
      console.log('Start session response:', response.data);
      return response.data;
    } catch (error: any) {
      console.error('Start session error details:', {
        message: error.message,
        response: error.response?.data,
        status: error.response?.status,
        statusText: error.response?.statusText,
        url: error.config?.url
      });
      
      return {
        success: false,
        message: 'Failed to start session',
        error: error.response?.data?.error || error.response?.data?.message || error.message || 'Network error',
      };
    }
  }

  static async getActiveSession(): Promise<AuthResponse> {
    try {
      const response = await authApi.get('/checkin/active');
      return response.data;
    } catch (error: any) {
      return {
        success: false,
        message: 'Failed to get active session',
        error: error.response?.data?.error || error.message,
      };
    }
  }

  // New passwordless authentication methods
  static async passwordlessRegister(data: PasswordlessAuthRequest): Promise<AuthResponse> {
    try {
      console.log('AuthService.passwordlessRegister called with data:', data);
      console.log('Making API call to:', '/auth/passwordless/register');
      const response = await authApi.post('/auth/passwordless/register', data);
      console.log('API response:', response.data);
      return response.data;
    } catch (error: any) {
      console.error('AuthService.passwordlessRegister error:', error);
      console.error('Error response:', error.response?.data);
      return {
        success: false,
        message: 'Passwordless registration failed',
        error: error.response?.data?.error || error.message,
      };
    }
  }

  static async passwordlessLogin(data: PasswordlessLoginRequest): Promise<AuthResponse> {
    try {
      const response = await authApi.post('/auth/passwordless/login', data);
      return response.data;
    } catch (error: any) {
      return {
        success: false,
        message: 'Passwordless login failed',
        error: error.response?.data?.error || error.message,
      };
    }
  }

  static async verifyOTP(data: OTPVerificationRequest): Promise<AuthResponse> {
    try {
      const response = await authApi.post('/auth/passwordless/verify', data);
      
      if (response.data.success && response.data.data?.token) {
        localStorage.setItem('auth_token', response.data.data.token);
        if (response.data.data.refreshToken) {
          localStorage.setItem('refresh_token', response.data.data.refreshToken);
        }
      }
      
      return response.data;
    } catch (error: any) {
      return {
        success: false,
        message: 'OTP verification failed',
        error: error.response?.data?.error || error.message,
      };
    }
  }

  static async resendOTP(data: PasswordlessLoginRequest): Promise<AuthResponse> {
    try {
      const response = await authApi.post('/auth/passwordless/resend', data);
      return response.data;
    } catch (error: any) {
      return {
        success: false,
        message: 'Failed to resend OTP',
        error: error.response?.data?.error || error.message,
      };
    }
  }

  static async forgotPassword(data: { email?: string; phone?: string }): Promise<AuthResponse> {
    try {
      const response = await authApi.post('/auth/forgot-password', data);
      return response.data;
    } catch (error: any) {
      return {
        success: false,
        message: 'Password reset request failed',
        error: error.response?.data?.error || error.message,
      };
    }
  }
} 